import React, {useEffect} from "react";
import {ChevronDown} from "lucide-react";
import {DropdownList} from "components/common/dropdown/DropdownList.component";
import {IDropdownListItem} from "types/gaiaComponents/dropdownButton";
import translate from "../translations/translations.wrapper.jsx";
import {ICON_SIZE} from "components/common/constants";

interface RenderTabsProps<T = any> {
    items: Array<IDropdownListItem>;
    activeTab: number | null;
    hasMore?: boolean;
    maxVisibleTabs?: number;
    translate: React.FC;
}

export const Tabs = <T, >({
                              items,
                              activeTab,
                              hasMore,
                              maxVisibleTabs,
                              translate
                          }: RenderTabsProps<T>) => {

    const [showMore, setShowMore] = React.useState(false);
    const visibleTabs = maxVisibleTabs ?? items.length;
    const moreRef = React.useRef<HTMLLIElement>(null);

    useEffect(() => {
        if (!showMore) return;

        function handleClickOutside(event: MouseEvent) {
            if (moreRef.current && !moreRef.current.contains(event.target as Node)) {
                setShowMore(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showMore]);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLLIElement>, item: IDropdownListItem) => {
        if (!items || items.length === 0) return;

        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            item.onClick && item.onClick();
        }
    }

    const handleShowMoreKeyDown = (event: React.KeyboardEvent<HTMLLIElement>) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setShowMore(!showMore);
        }
    }

    if (!items || !Array.isArray(items)) return <li></li>;
    if (items.length === 0) return <li></li>;

    return (
        <ul
            className="ga-tabs ga-tabs--horizontal ga-tabs--keyline bg-white m-0 px-0 attachments-preview-modal__tabs"
            role="tablist"
            aria-label="Sample Tabs"
            data-testid="__tst_tabs">
            {items.slice(0, visibleTabs).map((item, index) => (
                <li className={`ga-tabs__tab ${(activeTab === index) ? "ga-tabs__tab--selected" : ""}`}
                    key={item.key || index} tabIndex={0} role="tab"
                    onKeyDown={(e) => handleKeyDown(e, item)}>
                    <a className={"outline-none text-decoration-none"}
                       role="tab"
                       data-toggle="tab" aria-controls="normal"
                       aria-selected="false"
                       data-testid={`__tst_tab_${item.key}`}
                       onClick={item.onClick}>
                        {item.text}
                    </a>
                </li>
            ))}

            {hasMore && (<li ref={moreRef}
                             className={`ga-tabs__tab ${(activeTab >= visibleTabs) ? "ga-tabs__tab--selected" : ""}`}
                             tabIndex={0} onKeyDown={handleShowMoreKeyDown}>
                <span className={"d-flex"} onClick={() => {
                    setShowMore(!showMore)
                }}>
                   {translate("menu.more")}
                    <ChevronDown size={ICON_SIZE.SMALL} className="ga-tabs__tab-icon"/>
                </span>
                {showMore && (
                    <div className="h-inherit">
                        <DropdownList
                            class={"mt-2"}
                            items={items.slice(visibleTabs, items.length).map((item, index) => ({
                                onClick: item.onClick,
                                text: item.text,
                                dataCy: `__tst_tab_${item.key || index + visibleTabs}`,
                                selected: (activeTab === (visibleTabs + index)),
                                key: item.key || (index + visibleTabs).toString()
                            })) || []} position={undefined}/>
                    </div>)
                }
            </li>)
            }
        </ul>

    );
};

const withTranslations = translate(Tabs);
export default withTranslations;